// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld/base/core';
import L from 'leaflet';
import 'myWorld/base/localisation';
import 'myWorld/base/util';
import 'myWorld/features/featureRepresentation';
import 'myWorld/layers/layerManager';
import 'L.ContextMenu';
import MapInteractionMode from './mapInteractionMode';
import CtrlDragBox from './ctrlDragBox';

myw.MapControl = L.Map.extend({
    includes: myw.LocalisationMixin,

    options: {
        selectTolerance: 8, //in pixels
        touchSelectTolerance: 13, //in pixels
        contextmenu: true,
        contextmenuWidth: 160
    },

    // Actions available to context menu
    actions: {
        refresh: {
            action: 'redraw'
        },
        copyCoordinate: {
            action: '_copyCoordinate'
        },
        clearSelection: {
            action: '_clearSelection'
        },
        multipleSelect: {
            action: '_toggleMultipleSelect',
            checked: 'multipleSelect'
        }
    },

    /**
     * @class  Extension to the [Leaflet Map object]{@link http://leafletjs.com/reference.html#map-usage}  to be used as a superclass to the Geo map as well as
     * other maps such as internals. <br/>
     * Adds support for feature highlighting, better basemap control, and single-click event <br/>
     * @param {string|domElement}   divID       The div element (or it's id) to render the map onto.
     * @param {string}              worldId     The name of the world this map will be displaying.
     * @param {Object}              mapOptions  See {@link http://leafletjs.com/reference.html#map-options}
     * @constructs
     */
    initialize(app, divID, worldId, mapOptions) {
        mapOptions = mapOptions || {};
        const pointLabelsPaneName = 'point-labels';
        const layerManagerOptions = {
            textPaneName: pointLabelsPaneName
        };

        /** the myWorld application instance of which self is part of
         * @type {Application} */
        this.app = app;

        /** Deals with layers
         * @type {LayerManager} */
        this.layerManager = new myw.LayerManager(this, layerManagerOptions);

        /** Name of the world self is displaying. "geo" means geographic world
         * @type {string} */
        this.worldId = worldId;

        /** Highlight representations of features on the map. Keyed on urn
         * @type {Object<FeatureRepresentation>} */
        this.featureRepresentations = {};

        /**  Available background maps. Keyed on layer name.
            @type {Object<L.Layer>} */
        this.baseMaps = {};

        /** Promise alternative to Leaflet's whenReady method
         * @type {promise} */
        this.ready = new Promise(resolve => {
            this.whenReady(resolve);
        });

        this.extraPanes = {};

        this._currentBaseMap = null;
        this._currentBaseMapName = null;

        // holds the interaction modes as a stack so that we can swap back to the previous one
        // initialized with a default mode doesn't do anything on map click
        this._interactionModeStack = [new MapInteractionMode(this)];

        //by default use standard zoomControler on touch devices and slider control on non-touch devices
        const useZoomSlider = L.Control.Zoomslider && !myw.isTouchDevice;
        _.extend(mapOptions, { zoomControl: !useZoomSlider, zoomsliderControl: useZoomSlider });

        // Enable map tracking when panning to a lattitude of +/- 180deg so features can be selected
        _.extend(mapOptions, { worldCopyJump: true });

        // Build the map
        L.Map.prototype.initialize.call(this, divID, mapOptions);

        // Localise the zoom slider tooltip help.
        $('.leaflet-control-zoomslider-out').attr('title', myw.msg('MapControl', 'zoom_out'));
        $('.leaflet-control-zoomslider-in').attr('title', myw.msg('MapControl', 'zoom_in'));

        // Modify the leaflet attribution control to make its hyperlink open in another tab/window
        this.attributionControl.setPrefix('');

        //setup app event handlers

        //ENH: optimize for filter event?
        this.app.on(
            'currentFeature-changed currentFeatureSet-changed currentFeatureSet-filtered',
            this.handleCurrentFeatureSet.bind(this)
        );

        this.app.on('highlight-feature', e => {
            this.highlightFeature(e.feature);
        });

        this.app.on('unhighlight-feature', e => {
            this.unHighlightFeature(e.feature);
        });

        this.addHandler('ctrlDragBox', CtrlDragBox);
        this.ctrlDragBox.enable();

        // Setup handlers for map click events.
        // the click event has a delay so that if the user double-clicks, the second click can be cancelled
        let insideDblClickTime = false;

        // the following code setups a new event: "single-click" which will be fired only when the user does a single click
        // as oposed to the leaflet 'click' event which is fired twice on a double-click
        const self = this;
        let doubleEmit = false;
        this.on('click', event => {
            //  iOS 14 seems to emitting duplicate click events. Cancel any that duplicate here
            if (doubleEmit) {
                return;
            }
            else {
                doubleEmit = true;
                setTimeout(() => {
                    doubleEmit = false;
                },100);
            }
            // Process the map click event.
            if (this._discardNextClickEvt) {
                //end of box select drag
                //ideally a click wouldn't be fired in this situation but boxSelect would need to use draggable or boxZoom for Leaflet not to fire it.
                //here we prevent 'single-click' from being fired, which is enough for our handling of map clicks
                this._discardNextClickEvt = false;
                return;
            } else if (insideDblClickTime) {
                //second click of a double click
                insideDblClickTime = false; //this shouldn't be necessary, but in some browsers the timeout that sets this variable to false was not being executed
                clearTimeout(self._singleClickTimeout);
            } else {
                //fire the single-click event with a delay, otherwise the leafet 'dblclick' event might not be fired)
                self._singleClickTimeout = setTimeout(() => {
                    self.fire('single-click', event);
                }, 350);
                insideDblClickTime = true; //record that we are in the single-click period so we don't fire this event again
                setTimeout(() => {
                    //double-click time has gone past
                    insideDblClickTime = false;
                }, 350);
            }
        });

        this.on('baselayerchange', e => {
            this.handleBaseMapChange(e.layer.display_name);
        });

        this.on('single-click', this.handleMapClick, this);

        this.on('ctrldragbox-end', this.handleCtrlDragBox, this);

        this.on('moveend', this.checkFeatureRepresentations, this);

        //prevent DOM elements selection in IE. In IE8 doing a shift drag zoom could cause several DOM elements to become selected
        this.getContainer().onselectstart = () => false;
        this._createPane('svgs', 420);
        this._createPane('edit', 599);
        this._createPane(pointLabelsPaneName, 610);

        this._applyIPadOSHack(divID);
    },

    /**
     * As of 27/09/2019, iPadOS 13 breaks the context menu in LeafletJS.
     * This function applies hacks to get it working again if running iPadOS 13
     * Solution copied from https://github.com/Leaflet/Leaflet/issues/6817#issuecomment-535523415
     */
    _applyIPadOSHack(divID) {
        const isIOS13 =
            (navigator.platform == 'iPad' && navigator.userAgent.match(/ CPU OS 13/)) ||
            (navigator.platform == 'iPhone' && navigator.userAgent.match(/ CPU iPhone OS 13/));
        if (isIOS13) {
            // This is our main map el
            let mapEl;
            if (typeof divID === 'string') {
                mapEl = document.getElementById(divID);
            } else {
                mapEl = divID;
            }

            // This class adds 	-webkit-user-select: none; -webkit-touch-callout: none;
            // to the BODY el
            mapEl.classList.add('map-ios13');

            let timer = null;

            const fireLongPressEvent = originalEvent => {
                clearLongPressTimer();

                const el = originalEvent.target,
                    x = originalEvent.touches[0].clientX,
                    y = originalEvent.touches[0].clientY;

                // This will emulate contextmenu mouse event
                const event = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    clientX: x,
                    clientY: y
                });

                // fire the long-press event
                const suppressClickEvent = el.dispatchEvent.call(el, event);

                if (suppressClickEvent) {
                    // temporarily intercept and clear the next click
                    mapEl.addEventListener(
                        'touchend',
                        function clearMouseUp(e) {
                            mapEl.removeEventListener('touchend', clearMouseUp, true);
                            cancelEvent(e);
                        },
                        true
                    );
                }
            };

            const startLongPressTimer = e => {
                clearLongPressTimer(e);
                timer = setTimeout(fireLongPressEvent.bind(null, e), 1000);
            };

            const clearLongPressTimer = () => {
                if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            };

            const cancelEvent = e => {
                e.stopImmediatePropagation();
                e.preventDefault();
                e.stopPropagation();
            };

            // hook events that clear a pending long press event
            mapEl.addEventListener('touchcancel', clearLongPressTimer, true);
            mapEl.addEventListener('touchend', clearLongPressTimer, true);
            mapEl.addEventListener('touchmove', clearLongPressTimer, true);

            // hook events that can trigger a long press event
            mapEl.addEventListener('touchstart', startLongPressTimer, true); // <- start
        }
    },

    getCurrentFeature() {
        return this._currentFeature;
    },

    getCurrentFeatureRepresentation() {
        return this.getFeatureRepFor(this._currentFeature);
    },

    _createPane(name, zIndex) {
        if (!this.extraPanes[name]) {
            const newPane = this.createPane(name);
            newPane.style.zIndex = zIndex;
            newPane.style.pointerEvents = 'none';
            this.extraPanes[name] = newPane;
        }
    },

    /**
     * Returns the maximum zoom level of the map (if set in the `maxZoom` option of the map or of any layers).
     * Overridden to use information from layers in this.layerManager.
     * This way it can be used in initialization before layers are actually added to the map
     * @override
     * @return {integer}
     */
    getMaxDefinedZoom() {
        let maxZoom = L.Map.prototype.getMaxZoom.call(this);
        if (maxZoom == Infinity) {
            const layersZoom = _.map(
                this.layerManager.layers,
                layer => layer.layerDef.options.maxZoom
            );
            const maxLayersZoom = _.max(layersZoom);
            if (maxLayersZoom > 0) maxZoom = maxLayersZoom;
        }
        return maxZoom;
    },

    /**
     * Overridden to handle the case when the layers are not yet added on the map and the zoom level is infinity
     * Returns the geographical bounds visible in the current map view
     * @return LatLngBounds
     */

    getBounds() {
        const bounds = this.getPixelBounds();
        if (bounds.getTopRight().x === Infinity) {
            return L.latLngBounds([-90, -180], [90, 180]);
        }
        const sw = this.unproject(bounds.getBottomLeft()),
            ne = this.unproject(bounds.getTopRight());

        return L.latLngBounds(sw, ne);
    },

    /**
     * Redraws the overlays and basemap
     */
    redraw() {
        this._currentBaseMap.redraw();
        this.layerManager.redraw();
    },

    /**
     * Passes the click event to the interaction mode stack.
     * If an interaction mode signals that it didn't handle the event by returning false, the event is passed on to the next element on the stack
     * @param  {MouseEvent} event Mouse click event
     */
    handleMapClick(event) {
        // pass the click event to interaction modes on the stack, starting with the top one
        // when an interaction mode returns true, meaning it has handled the event, the others don't get the event
        _.reduceRight(
            this._interactionModeStack,
            (previouslyHandled, interactionMode) => {
                // Sometimes a handler might cause the mode stack to be popped more than once and _reduceRight provides
                // undefined to this callback.
                if (!interactionMode) return false;

                if (previouslyHandled) {
                    return true;
                } else {
                    return interactionMode.handleMapClick(event) !== false;
                }
            },
            false
        );
    },

    /**
     * Passes the end of a ctrl+drag box event to the interaction mode stack.
     * If an interaction mode signals that it didn't handle the event by returning false, the event is passed on to the next element on the stack
     * @param  {MouseEvent} event Mouse click event
     */
    handleCtrlDragBox(event) {
        // pass the click to interaction modes on the stack, starting with the top one
        // when an interaction mode returns true, meaning it has handled the event, the others don't get the event
        _.reduceRight(
            this._interactionModeStack,
            (previouslyHandled, interactionMode) => {
                // Sometimes a handler might cause the mode stack to be popped more than once and _reduceRight provides
                // undefined to this callback.
                if (!interactionMode || !interactionMode.handleCtrlDragBox) return false;

                if (previouslyHandled) {
                    return true;
                } else {
                    return interactionMode.handleCtrlDragBox(event) !== false;
                }
            },
            false
        );
        this._discardNextClickEvt = true;
    },

    /**
     * Obtains the features selectable at a given location.
     * Sends a selection request to the database and additional requests to any layers that provide an external selection
     * @param  {L.LatLng}   selectionPoint    LatLng selected by the user
     * @param  {integer}    [zoomLevel]       Zoom level to use when calculating a radius. Default is current zoom level of the map
     * @param  {integer}    [pixelTolerance]  Tolerance in pixels to use when calculating a radius. Default is 8 (or 13 for touch devices)
     * @returns {Promise<Array<Feature>>} Features selectable at the provided point
     */
    selectFeatures(selectionPoint, zoomLevel, pixelTolerance) {
        const worldId = this.worldId == 'geo' ? null : this.worldId,
            layers = this.getVisibleLayers();

        zoomLevel = zoomLevel || Math.round(this.getZoom());
        pixelTolerance =
            pixelTolerance ||
            (L.Browser.touch ? this.options.touchSelectTolerance : this.options.selectTolerance);

        return this.app.database.select(selectionPoint, zoomLevel, pixelTolerance, layers, worldId);
    },

    /**
     * Sends a selection request to the database to the select_within service to select all features within the geom
     * @param {string} boxGeom Geometry in list of lng,lat pairs, as string
     */
    selectBox(boxGeom) {
        const worldId = this.worldId == 'geo' ? null : this.worldId,
            layers = this.getVisibleLayers();

        const zoomLevel = Math.round(this.getZoom());
        return this.app.database.selectBox(boxGeom, zoomLevel, layers, worldId);
    },

    /**
     * Returns an array currently displayed overlay layers
     * @return {array<Layer>}
     */
    getVisibleLayers() {
        return this.layerManager.getVisibleLayers(this.getZoom());
    },

    /**
     * Returns an array of ids describing the currently displayed overlay layers
     * The layer has to be turned on and the configuration for the layer has to match the current zoom level
     * @return {string[]} List with ids(codes) of the layers
     */
    getVisibleLayerIds() {
        const visibleLayers = this.getVisibleLayers();
        //get codes and remove empty codes
        return _.compact(_.invoke(visibleLayers, 'getCode'));
    },

    /**
     * Returns an array of ids describing the currently turned ON overlay maps
     * @return {string[]} List with ids(codes) of the layers
     */
    getCurrentLayerIds() {
        return this.layerManager.getCurrentLayerIds();
    },

    /** Sets the current basemap */
    setCurrentBaseMap(baseMapName) {
        const curBaseMap = this.getCurrentBaseMap();
        const newBaseMap = this.baseMaps[baseMapName];
        if (newBaseMap && curBaseMap != newBaseMap) {
            //first remove the current base map

            if (curBaseMap) this.removeLayer(curBaseMap);

            this._currentBaseMapName = baseMapName;
            this._currentBaseMap = newBaseMap;
            this.addLayer(this._currentBaseMap, true);

            //leaflet fires the event if the the change is originated from its layer control
            //if the change is originated by calling this method the event should also be fired
            this.fire('baselayerchange', { layer: newBaseMap });
        }
    },
    /**
     *  Handles a user changing the baseMap when he clicks the Layer control
     *  Updates internal currentBaseMap variables to keep track of the change
     */
    handleBaseMapChange(baseMapName) {
        this._currentBaseMapName = baseMapName;
        this._currentBaseMap = this.baseMaps[baseMapName];
    },

    /** get the current baseMap name */
    getCurrentBaseMapName() {
        return this._currentBaseMapName;
    },
    /** get current basemap info */
    getCurrentBaseMap() {
        return this._currentBaseMap;
    },

    setCurrentMapViewParameters(aView) {
        // Set current map view with given aView
        const latlng = aView['center'];
        this.setView(latlng, aView['zoom']);
    },

    /**
     * @return {Object} keys: center, zoom
     */
    getMapViewParameters() {
        return {
            center: this.getCenter(),
            zoom: this.getZoom()
        };
    },

    setWorld(worldId) {
        this.worldId = worldId;
        this.removeFeatureReps(_.pluck(this.featureRepresentations, 'feature'));
        this._currentFeatureSet = null;
        this._currentFeature = null;
    },

    // update current featureSet representations
    handleCurrentFeatureSet(e) {
        const app = this.app;
        const feature = app.currentFeature;

        //handle representation of feature set
        const newFeatures = app.currentFeatureSet.items;
        if (newFeatures !== this._currentFeatureSet) {
            //feature set (or filter) is changing, not just current feature
            this.removeFeatureReps(_.pluck(this.featureRepresentations, 'feature'));
            const eventHandlers = this._getRepEventHandlers();
            this.createFeatureReps(newFeatures, null, eventHandlers);
            this._currentFeatureSet = newFeatures;
        }

        //handle representation of current feature
        if (feature != this._currentFeature) {
            if (this._currentFeature) this.unHighlightFeature(this._currentFeature);
            this._currentFeature = feature;
        }
        const isWorldOwner = feature && feature.isWorldOwner(this.worldId);
        if (feature && !isWorldOwner) this.highlightFeature(feature);

        if (e.zoomTo) this.zoomTo(feature);
    },

    _getRepEventHandlers() {
        //ENH: create event handlers just once
        const app = this.app;
        const self = this;
        let eventHandlers;
        const clickEventHandler = [
            {
                eventName: 'click',
                callback(feature, rep, evt) {
                    self.currentInteractionMode().handleFeatureClick(feature, rep, evt);
                }
            }
        ];
        const mouseEventsHandlers = [
            {
                eventName: 'mouseover',
                callback(feature, rep) {
                    self.highlightFeature(feature);
                }
            },
            {
                eventName: 'mouseout',
                callback(feature, rep) {
                    if (app.currentFeature != feature) self.unHighlightFeature(feature);
                }
            }
        ];
        if (app.currentFeature) {
            eventHandlers = clickEventHandler;
        } else {
            eventHandlers = _.union(clickEventHandler, mouseEventsHandlers);
        }
        return eventHandlers;
    },

    /**
     * Highlights a feature on the map. (using default highlight style)
     * Creates a representation if necessary
     * @param  {Feature} feature
     */
    highlightFeature(feature) {
        const urn = feature.getUrn(true);
        let featureRep = this.featureRepresentations[urn];
        if (!featureRep) {
            //create a feature representation and display it on the map view
            featureRep = this.createFeatureRep(feature, undefined, []);
        }
        // highlight if visible on map
        if (featureRep) featureRep.highlight();
    },

    /**
     * Removes the highlighting of a feature from the map
     * @param  {Feature} feature
     */
    unHighlightFeature(feature) {
        const urn = feature.getUrn(true);
        const featureRep = this.featureRepresentations[urn];
        if (featureRep) featureRep.unHighlight();
    },

    /**
     * Creates a set of feature representations and adds them to the map
     * @param  {Array<Feature>}     features        Features to represent on the map
     * @param  {Array<styleDefinition>}           styles          The styles to be applied to the {@link FeatureRepresentation}
     * @param  {Array<eventHandler>}    eventHandlers   Event handlers for the {@link FeatureRepresentation}
     * @return {Array<FeatureRepresentation>}  Representations for the features
     */
    createFeatureReps(features, styles, eventHandlers) {
        const featureReps = [];
        _.each(features, feature => {
            const isWorldOwner = feature && feature.isWorldOwner(this.worldId);
            if (isWorldOwner) return; //we don't want a feature rep for the world owner (we just get a blue polygon covering everything)

            const rep = this.createFeatureRep(feature, styles, eventHandlers);
            if (rep) {
                featureReps.push(rep);
            }
        });
        return featureReps;
    },

    /**
     * Creates a map representation for a given feature and adds it to the map view. <br/>
     * If feature has geometry in the world this map is looking at, then use that geometry. Otherwise
     * find an owning feature that does have geometry in this world <br/>
     * @param  {Feature}            feature       The feature to display on the map
     * @param  {styleDefinition}        styles        The styles to be applied to the {@link FeatureRepresentation}
     * @param  {Array<eventHandler>}    eventHandlers Event handlers for the {@link FeatureRepresentation}
     * @return {Array<FeatureRepresentation>}  Representations for the features
     */
    createFeatureRep(feature, styles, eventHandlers) {
        const mapWorldId = this.worldId;
        let rep;
        if (!eventHandlers) eventHandlers = [];

        styles = styles || feature.getCurrentFeatureStyleDef();

        const geom = feature.getGeometryInWorld(mapWorldId);
        if (geom) {
            rep = this._createFeatureRep(feature, eventHandlers, styles);
            if (rep) {
                const tooltip = feature.tooltip ? feature.tooltip() : this._getTooltipFor(feature);
                rep.bindTooltip(tooltip);
            }
            return rep;
        } else if (mapWorldId == 'geo') {
            // We do not attempt to navigate up containment hierarchy to find 'geo' position because
            // internals features should have had a geo_geometry value provided by the server.
        } else {
            //ENH: Create feature representation for owner of self in this world using containement hierarchy provided by server (as per geo_geometry)
            //Doing it from client will be innefective if a lot of features are being highlighted
        }
    },

    /**
     * Creates a map representation for a given feature and adds it to the map view. <br/>
     * It is assumed that the feature has a geometry in this world
     * @param  {Feature}            feature       The feature to display on the map
     * @param  {styleDefinition}        styles        The styles to be applied to the {@link FeatureRepresentation}
     * @param  {Array<eventHandler>}    eventHandlers Event handlers for the {@link FeatureRepresentation}
     * @return {Array<FeatureRepresentation>}  Representations for the features
     * @private
     */
    _createFeatureRep(feature, eventHandlers, styles) {
        const urn = feature.getUrn(true);
        let featureRep = this.featureRepresentations[urn];
        const options = {};
        options.eventHandlers = eventHandlers;
        options.styles = styles;
        options.worldName = this.worldId;
        options.paneName = 'svgs';
        if (!featureRep) {
            //ENH: probably should always create a new one and remove the old one in addFeatureRep
            featureRep = new myw.FeatureRepresentation(feature, options);
        }

        return this.addFeatureRep(featureRep);
    },

    /**
     * Adds an existing feature representation to the map
     * @param {FeatureRepresentation} featureRep
     * @return {Array<FeatureRepresentation>}  Representations for the features
     */
    addFeatureRep(featureRep) {
        const urn = featureRep.feature.getUrn(true);
        //ENH: if one rep already exists, remove it?
        this.featureRepresentations[urn] = featureRep;
        //only actually add the representation to the leaflet map if it will be visible
        //this has performance benefits and avoids the dom from having to manage overlays
        //that may be offset by millions of pixels (which caused issues with z-index)
        if (
            this.getBounds()
                .pad(0.5)
                .intersects(featureRep.getBounds())
        ) {
            featureRep.addToMap(this);
        }
        return featureRep;
    },

    /**
     * Remove from the map the representations for FEATURES
     * @param  {Array<Feature>} features features to remove from the map
     */
    removeFeatureReps(features) {
        const reps = this.featureRepresentations;
        let urn;
        _.each(
            features,
            feature => {
                urn = feature.getUrn(true);
                const rep = reps[urn];
                if (rep) {
                    delete reps[urn];
                    rep.removeFromMap();
                }
            },
            this
        );
    },

    /**
     * Checks if the current feature set representations should be on the map and
     * adds them or removes them as appropriate. <br/>
     * @private
     */
    checkFeatureRepresentations() {
        //map bounds are enlarged by 50% so that when panning (and zooming out) the representations
        //are already on the map instead of only showing when the operation finishes
        const mapBounds = this.getBounds().pad(0.5);
        _.each(this.featureRepresentations, (rep, urn) => {
            if (!rep.map && mapBounds.intersects(rep.getBounds())) {
                rep.addToMap(this);
            } else if (rep.map && !mapBounds.intersects(rep.getBounds())) {
                rep.removeFromMap();
            }
        });
    },

    /**
     * @param  {Feature} feature
     * @return {FeatureRepresentation} Representation of FEATURE in self
     */
    getFeatureRepFor(feature) {
        if (!feature) return;
        const urn = feature.getUrn(true);
        let rep = this.featureRepresentations[urn];
        if (!rep) rep = this.createFeatureRep(feature); //TODO: it's missing event handlers
        return rep;
    },

    /**
     * Pan and zoom the map to given feature
     * When zooming to a point geometry, it will set the zoom to 18 unless the current zoom
     * is already higher than that
     * @param  {string|Feature|FeatureRepresentation} featureOrfeatureUrn The feature (or its id) to zoom to. Urn should only be used if feature is already represented on the map
     */
    zoomTo(featureOrfeatureUrn) {
        const prevZoomLevel = this.getZoom();
        let featureRep;

        if (featureOrfeatureUrn instanceof myw.FeatureRepresentation) {
            featureRep = featureOrfeatureUrn;
        } else if (typeof featureOrfeatureUrn == 'string') {
            featureRep = this.featureRepresentations[featureOrfeatureUrn];
        } else if (featureOrfeatureUrn instanceof myw.Feature) {
            featureRep = this.featureRepresentations[featureOrfeatureUrn.getUrn(true)];
            if (!featureRep && featureOrfeatureUrn.getGeometryInWorld(this.worldId)) {
                const options = {};
                options.worldName = this.worldId;
                featureRep = new myw.FeatureRepresentation(featureOrfeatureUrn, options);
            }
        }

        if (featureRep) {
            const bounds = featureRep.feature.bounds;
            if (bounds) {
                this.fitBounds([[bounds[1], bounds[0]], [bounds[3], bounds[2]]]);
            } else if (featureRep.getGeometryType() == 'Point' && prevZoomLevel < 19) {
                // when zooming to a point, the zoom level will be 20, so let's zoom out
                this.setView(featureRep.getCenter(), 18);
            } else {
                this.fitBounds(featureRep.getBounds(), { maxZoom: this.getMaxDefinedZoom() });
            }
        }
    },

    /**
     * Fits the bounds of the map to include the map representations of the given features
     * @param  {Array<Feature>} features A list with features to see on the map
     */
    fitBoundsToFeatures(features) {
        let bounds = null;
        _.each(features, feature => {
            const urn = feature.getUrn(true);
            let featureRep = this.featureRepresentations[urn];
            let featureBounds;

            if (!featureRep && feature.hasGeometry()) {
                featureRep = new myw.FeatureRepresentation(feature);
            }

            if (featureRep) {
                featureBounds = featureRep.getBounds();
                if (!bounds)
                    bounds = L.latLngBounds(
                        featureBounds.getSouthWest(),
                        featureBounds.getNorthEast()
                    );
                else bounds.extend(featureBounds);
            }
        });
        if (bounds) this.fitBounds(bounds);
    },

    /**
     * Obtains a bounding box to represent a selection area
     * @param  {L.LatLng}   selectionPoint  Point of the selection
     * @param  {integer}    zoomLevel       Zoom level at time of selection
     * @param  {integer}    pixelTolerance  Tolerance in pixels
     * @return {L.LatLngBounds}
     */
    getBoundingBoxFor(selectionPoint, zoomLevel, pixelTolerance) {
        const pixelPoint = this.project(selectionPoint, zoomLevel);
        const swPixel = pixelPoint.add([-pixelTolerance, -pixelTolerance]);
        const nePixel = pixelPoint.add([pixelTolerance, pixelTolerance]);
        const sw = this.unproject(swPixel, zoomLevel);
        const ne = this.unproject(nePixel, zoomLevel);
        return L.latLngBounds(sw, ne);
    },

    currentInteractionMode() {
        return _.last(this._interactionModeStack);
    },

    /**
     * Enables a interaction mode
     * Disables the previous one and keep tracks of it so it can be re-enabled later
     * @param {MapInteractionMode} InteractionMode the map interaction mode to use
     */
    setInteractionMode(interactionMode) {
        //disable previous mode
        const stack = this._interactionModeStack;
        if (stack.length) _.last(stack).disable();

        //add and enable new mode
        stack.push(interactionMode);

        return interactionMode.enable();
    },

    /**
     * Disables the current interaction mode and enables the previous one
     */
    endCurrentInteractionMode() {
        const stack = this._interactionModeStack;
        const popedMode = stack.pop();
        popedMode.disable();
        //enable the new current mode
        if (stack.length) _.last(stack).enable();
    },

    /**
     * Creates tooltip that appears when selected object is hovered.
     * @param {MywFeatureRep} feature
     */
    _getTooltipFor(feature) {
        const deltaDesc = feature.isDeltaSchema() && feature.getDeltaDescription();
        return `<dl><dd class='result-title'>${feature.getTitle()}</dd><dd>${feature.getShortDescription()}</dd>${
            deltaDesc ? '<dd>[' + deltaDesc + ']</dd>' : '' //Square brackets for display
        }</dl>`;
    },

    _clearSelection() {
        this.app.clearResults();
    },

    /**
     * Copies a string with the lat/lng to the clipboard
     * Called when the corresponding context menu action is selected by the user
     * @param  {object} location Click location from context menu
     * @private
     */
    _copyCoordinate(location) {
        location = myw.Util.formatLatLng(location.latlng, 7);
        myw.Util.copyToClipboard(location);
    },

    /**
     * Toggles multiple select
     * Called when the context menu action is selected by the user
     */
    _toggleMultipleSelect() {
        if (!this.multipleSelect) {
            this.currentInteractionMode().setMultipleSelect(true);
            this.multipleSelect = true;
        } else {
            this.multipleSelect = null;
            this.currentInteractionMode().setMultipleSelect(false);
        }
    },

    /**
     * @return {jqueryElement} The map container element
     */
    getDivElement() {
        return $(this.getContainer());
    },

    /**
     * Shows the map
     */
    show() {
        this.getDivElement().show();
        this.invalidateSize();
    },

    /**
     * Hides the map
     */
    hide() {
        this.getDivElement().hide();
    },

    /**
     * Applies the provided css properties to the map div
     * @param  {object} options Css properties to apply
     */
    css(options) {
        this.getDivElement().css(options);
    },

    /**
     * Override in order to maintain the map center
     * @override
     */
    invalidateSize() {
        let center;
        if (this._loaded) center = this.getCenter();
        //call super
        L.Map.prototype.invalidateSize.call(this);

        if (center) this.panTo(center);

        return this;
    },

    /**
     * Obtains the current state of map <br/>
     * So it can be restored on the next session/initialization
     */
    getState() {
        let center, zoom;

        try {
            center = this.getCenter();
            zoom = this.getZoom();
        } catch (e) {
            // map may not have been initialized if there was some problem at startup
        }

        return {
            center: center,
            zoom: zoom,
            basemapName: this.getCurrentBaseMapName()
        };
    }
});

export default myw.MapControl;
